import { isApiError, type admin } from '@repletoryreach/sdk-api';

/**
 * The price ladder's arithmetic (owner's ruling, 21 September 2026), mirrored
 * from the billing service's `PlanPricing` and `PlanLadder` so a dialog can
 * show a floor, a ceiling or a plan's price while the operator is still typing.
 *
 * The service stays the judge. Every figure here is a preview of what it will
 * accept, and a refusal still comes back with the service's own numbers (see
 * {@link ladderRefusal}). Money is whole minor units throughout and the
 * rounding is the service's: a floor rounds up to the cent, a ceiling down.
 */

type Plan = admin.AdminPlan;

/** What the service assumes when no plan is on sale to read a markup from. */
export const DEFAULT_MARKUP_PERCENT = 100;
export const MAX_MARKUP_PERCENT = 1000;

/** One credit is R1 of messaging, held as 100 minor units. */
export const MINOR_PER_CREDIT = 100;

const clampMarkup = (percent: number) => Math.min(Math.max(percent, 0), MAX_MARKUP_PERCENT);

/** Integer division that rounds up, as the service does it. */
const ceilDiv = (numerator: number, denominator: number) =>
  numerator <= 0 ? 0 : Math.floor((numerator + denominator - 1) / denominator);

/** The least a unit may be sold for under a markup: its cost plus the markup, up to the cent. A unit that costs nothing has no floor. */
export function floorMinor(costMinor: number, markupPercent: number): number {
  return costMinor <= 0 ? 0 : ceilDiv(costMinor * (100 + clampMarkup(markupPercent)), 100);
}

/** The list price in force: the row's own, and never under the entry plan's floor. */
export function listInForce(listMinor: number, costMinor: number, entryMarkupPercent: number): number {
  return Math.max(Math.max(listMinor, 0), floorMinor(costMinor, entryMarkupPercent));
}

/**
 * What one unit costs on a plan. A price set above its floor is shared out in
 * the proportion the plan's markup is of the entry markup, and no plan is ever
 * dearer than pay-as-you-go or cheaper than its own floor.
 */
export function planCharge(listMinor: number, costMinor: number, planMarkupPercent: number, entryMarkupPercent: number): number {
  const entry = clampMarkup(entryMarkupPercent);
  const plan = Math.min(clampMarkup(planMarkupPercent), entry);
  const list = listInForce(listMinor, costMinor, entry);
  if (list <= 0) return 0;
  return Math.max(ceilDiv(list * (100 + plan), 100 + entry), floorMinor(costMinor, plan));
}

/**
 * The charge in force on a price book row: the service's `list_minor`, which is
 * never under the floor whatever the stored row says. The stored charge stands
 * in for a service that does not send it.
 */
export function chargeInForce(rate: { amount_minor: number; list_minor?: number }): number {
  return rate.list_minor ?? rate.amount_minor;
}

/** The most included credits a monthly fee can carry at a markup: fee x (1 + markup), rounded down. A free plan can carry none. */
export function includedCeilingMinor(feeMinor: number, markupPercent: number): number {
  return feeMinor <= 0 ? 0 : Math.floor((feeMinor * (100 + clampMarkup(markupPercent))) / 100);
}

const markupOf = (plan: Plan) => plan.markup_percent ?? DEFAULT_MARKUP_PERCENT;

/** The plans on sale, in the order the price list shows them. */
export function plansOnSale(plans: Plan[]): Plan[] {
  return plans
    .filter((p) => p.is_active)
    .sort((a, b) => a.sort_order - b.sort_order || a.monthly_amount_minor - b.monthly_amount_minor);
}

/**
 * The entry plan: the cheapest one on sale, exactly as the service finds it.
 * Its markup is the pay-as-you-go price, so it sets every list price and floor.
 */
export function entryPlan(plans: Plan[]): Plan | undefined {
  return plans
    .filter((p) => p.is_active)
    .sort((a, b) =>
      a.monthly_amount_minor - b.monthly_amount_minor
      || a.sort_order - b.sort_order
      || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))[0];
}

export function entryMarkupPercent(plans: Plan[]): number {
  const entry = entryPlan(plans);
  return entry ? markupOf(entry) : DEFAULT_MARKUP_PERCENT;
}

/**
 * What one unit costs on every plan on sale, keyed by plan code. The rows of
 * the price book carry this from the service (`plan_amounts_minor`); this is
 * for a charge that is still being typed and has no row yet.
 */
export function planAmounts(plans: Plan[], listMinor: number, costMinor: number): Record<string, number> {
  const entry = entryMarkupPercent(plans);
  const out: Record<string, number> = {};
  // A plan priced above the entry markup is measured against itself, so it
  // pays the list price and never more.
  for (const p of plansOnSale(plans)) out[p.code] = planCharge(listMinor, costMinor, markupOf(p), Math.max(entry, markupOf(p)));
  return out;
}

/** Credits as the figure they are: "848", or "848,30" when the minor units are not a whole credit. */
export function formatCredits(minor: number): string {
  const whole = minor % MINOR_PER_CREDIT === 0;
  return new Intl.NumberFormat('en-ZA', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(minor / MINOR_PER_CREDIT);
}

/** A ceiling in the whole credits an operator types: 84 830 minor units carry 848 credits. */
export function wholeCredits(minor: number): number {
  return Math.floor(minor / MINOR_PER_CREDIT);
}

/**
 * The numbers a refusal carries beside its code. The service puts them on the
 * error itself (`error.floor_minor`, `error.ceiling_minor`), not in `details`,
 * so they are read from the body. Each is checked before use: a body is data
 * from the wire, not a type.
 */
export interface LadderRefusal {
  code: string;
  sku_code?: string;
  cost_minor?: number;
  floor_minor?: number;
  entry_markup_percent?: number;
  ceiling_minor?: number;
  included_credits_minor?: number;
  markup_percent?: number;
}

const NUMBER_FIELDS = ['cost_minor', 'floor_minor', 'entry_markup_percent', 'ceiling_minor', 'included_credits_minor', 'markup_percent'] as const;

export function ladderRefusal(err: unknown): LadderRefusal | null {
  if (!isApiError(err)) return null;
  const error = (err.body as { error?: Record<string, unknown> } | null)?.error ?? {};
  const refusal: LadderRefusal = { code: err.code };
  for (const field of NUMBER_FIELDS) {
    const v = error[field];
    if (typeof v === 'number' && Number.isFinite(v)) refusal[field] = v;
  }
  if (typeof error.sku_code === 'string') refusal.sku_code = error.sku_code;
  return refusal;
}
